import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useDeleteGenericAccountMutation,
  useSearchClientSavedFiltersMutation,
  useSearchGenericAccountsMutation,
} from "../../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../../api/services/client/models";
import { MuiMenuList, WarningConfirmationModal } from "../../../components";
import { DeleteFilterModal } from "../../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../../components/searchFilters/saveFilter/SaveFilter";
import {
  MAX_SAVE_FILTERS_LIMIT,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../helpers/constants";
import {
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue,
} from "../../../helpers/utils";
import { useEnterKeyAction } from "../../../hooks/useEnterKeyAction";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { FilterProps } from "../../clientSearch/modelsAndSchemas/Models";
import { GenericAccountsSearchWrapper } from "./GenericAccountsSearch.style";
import { GenericAccountsSearchForm } from "./components/genericAccountsSearchForm/GenericAccountsSearchForm";
import { GenericAccountsSearchHeader } from "./components/genericAccountsSearchHeader/GenericAccountsSearchHeader";
import { GenericAccountsSearchResult } from "./components/genericAccountsSearchResult/GenericAccountsSearchResult";
import {
  GenericAccountsSearchFormKey,
  GenericAccountsSearchFormProps,
} from "./modelsAndSchemas/Models";
import { genericAccountsSearchSiteCoreKeys } from "./siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.GENERIC_ACCOUNTS_SEARCH;

const GenericAccountsSearch: FC = () => {
  const defaultValues = {
    [GenericAccountsSearchFormKey.genericAccountName]: "",
    [GenericAccountsSearchFormKey.genericAccountNameFilter]: "",
    [GenericAccountsSearchFormKey.purpose]: "",
    [GenericAccountsSearchFormKey.clientId]: "",
    [GenericAccountsSearchFormKey.clientName]: "",
    [GenericAccountsSearchFormKey.nextView]: "",
    [GenericAccountsSearchFormKey.country]: "",
    [GenericAccountsSearchFormKey.nextProgramClass]: "",
    [GenericAccountsSearchFormKey.deliveryOffice]: [],
    [GenericAccountsSearchFormKey.genericAccountStatus]: "",
    [GenericAccountsSearchFormKey.requestedBy]: "",
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [isOpenSaveFilterModal, setIsOpenSaveFilterModal] = useState(false);
  const [filtersData, setFiltersData] = useState<FilterProps[]>([]);
  const [showSavedFilter, setShowSavedFilter] = useState(false);
  const [filterToDeleteGenericAccount, setFilterToDeleteGenericAccount] =
    useState<MenuListParams>({
      id: "",
      name: "",
    });
  const [preloadFilter, setPreloadFilter] = useState(false);
  const [isMaxFilterReached, setIsMaxFilterReached] = useState(false);
  const [
    preloadFilterValuesGenericAccount,
    setPreloadFilterValuesGenericAccount,
  ] = useState({
    id: "",
    name: "",
    filters: filtersData,
  });
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [accountIdToDelete, setAccountIdToDelete] = useState<string | null>(
    null,
  );
  const deliveryOfficeRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const clientRef = useRef<{
    resetValues: (values?: string) => void;
  }>(null);

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const { commonLabels, fetchLabel } = useSiteCore();
  const {
    mutateAsync: getAccountList,
    isPending: getAccountListPending,
    isError: isGetAccountListError,
    data: clientSearchResult,
  } = useSearchGenericAccountsMutation();

  const {
    data: listOfFiltersGenericAccount,
    refetch: refetchListOfFiltersGenericAccount,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "GenericAccountSearch",
  });

  const {
    mutateAsync: deleteGenericAccountMutation,
    isPending: isDeleteGenericAccountPending,
  } = useDeleteGenericAccountMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: getAccountListPending || isDeleteGenericAccountPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [getAccountListPending, isDeleteGenericAccountPending]);

  useEffect(() => {
    if (isGetAccountListError) {
      const message = commonLabels.someThingWentWrongPleaseTryAgainLater;
      displayToast(message, "error");
    }
  }, [isGetAccountListError]);

  const methods = useForm({
    mode: "all",
    defaultValues: defaultValues,
  });

  const { getValues, reset, handleSubmit } = methods;

  useEffect(() => {
    handleAccountSearchClick();
  }, []);

  const prepareData = () => {
    const {
      genericAccountName,
      purpose,
      clientId,
      nextView,
      country,
      nextProgramClass,
      deliveryOffice,
      genericAccountStatus,
      requestedBy,
    } = getValues();

    return {
      accountName: genericAccountName ?? null,
      purposeId: purpose ?? null,
      clientId: clientId ?? null,
      solutionOfferingId: nextView ?? null,
      programClassId: nextProgramClass ?? null,
      countryId: isNotEmpty(country) ? country : null,
      deliveryOfficeIds: deliveryOffice,
      statusId: genericAccountStatus ?? null,
      requestedBy: isNotEmpty(requestedBy) ? requestedBy : null,
    };
  };

  const handleAccountSearchClick = async () => {
    setIsSearchClick(true);
    getAccountList(prepareData());
  };

  const handleClearAllSearchButton = () => {
    reset(defaultValues);
    if (deliveryOfficeRef.current) {
      deliveryOfficeRef.current.resetValues();
    }
    if (clientRef.current) {
      clientRef.current.resetValues();
    }
    removeAccountSavedFilters();
    handleAccountSearchClick();
  };

  const handleSetFilterModal = () => {
    setIsOpenSaveFilterModal(!isOpenSaveFilterModal);
  };

  const handleCloseEvent = () => {
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleAccountFilterRowClick = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    setPreloadFilter(true);
    const itemFilterGenericAccount = item as SaveSearchFilterParams;
    // if there is no value for country and previously some data exists, reset the country field
    deliveryOfficeRef?.current?.resetValues([]);
    
    itemFilterGenericAccount.filters.forEach((row) => {
      if (row.fieldName === GenericAccountsSearchFormKey.deliveryOffice) {
        // if the filter is country, split the value and convert it to array
        if (typeof row.fieldValue === "string") {
          row.fieldValue = row.fieldValue.split("|");
        }

        if (deliveryOfficeRef.current) {
          deliveryOfficeRef.current.resetValues(row.fieldValue);
        }
      }
    });
    setPreloadFilterValuesGenericAccount({
      id: itemFilterGenericAccount.id ?? "",
      name: itemFilterGenericAccount.name ?? "",
      filters: mapFiltersToFormValues(
        itemFilterGenericAccount.filters,
        defaultValues,
        GenericAccountsSearchFormKey,
      ),
    });
    reset(
      mapFiltersToFormValues(
        itemFilterGenericAccount.filters,
        defaultValues,
        GenericAccountsSearchFormKey,
      ),
    );
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const removeAccountSavedFilters = () => {
    setPreloadFilterValuesGenericAccount({ id: "", name: "", filters: [] });
    setPreloadFilter(false);
  };

  const handleDeleteFilter = (
    event: React.MouseEvent<HTMLButtonElement>,
    itemMenu: MenuListParams,
  ) => {
    event.stopPropagation();
    // if the selected filter is deleted, remove the saved filter details from the memory
    if (preloadFilterValuesGenericAccount.name === itemMenu.name) {
      removeAccountSavedFilters();
    }

    setFilterToDeleteGenericAccount({ id: itemMenu?.id, name: itemMenu?.name });
    if (showModal === false) {
      setShowModal(true);
    }
  };

  const handleFilterModalClose = () => {
    if (showModal) {
      setShowModal(false);
    }
  };

  const handleMultipleFilterDeleteModalClose = () => {
    if (showMultipleDeleteModal) {
      setShowMultipleDeleteModal(false);
    }
  };

  const handlePrimaryButtonClick = () => {
    setShowModal(false);
  };

  const handleMultipleFilterDeletePrimaryButtonClick = () => {
    setShowMultipleDeleteModal(false);
  };

  const handleSavedFilter = () => {
    if (showSavedFilter === false) {
      setShowSavedFilter(true);
    }
  };

  const closeSaveFilterModal = () => {
    setIsOpenSaveFilterModal(false);
  };

  const handleSaveFilterModal = () => {
    if (
      listOfFiltersGenericAccount &&
      listOfFiltersGenericAccount.length >= MAX_SAVE_FILTERS_LIMIT &&
      !preloadFilter
    ) {
      if (showMultipleDeleteModal === false) {
        setShowMultipleDeleteModal(true);
      }
    } else {
      handleSetFilterModal();
    }

    const values = getValues();

    const filters: {
      fieldName: keyof GenericAccountsSearchFormProps;
      operatorIdKey?: keyof GenericAccountsSearchFormProps;
    }[] = [
      {
        fieldName: GenericAccountsSearchFormKey.genericAccountName,
        operatorIdKey: GenericAccountsSearchFormKey.genericAccountNameFilter,
      },
      {
        fieldName: GenericAccountsSearchFormKey.purpose,
      },
      {
        fieldName: GenericAccountsSearchFormKey.clientId,
      },
      {
        fieldName: GenericAccountsSearchFormKey.clientName,
      },
      {
        fieldName: GenericAccountsSearchFormKey.nextView,
      },
      { fieldName: GenericAccountsSearchFormKey.country },
      {
        fieldName: GenericAccountsSearchFormKey.nextProgramClass,
      },

      { fieldName: GenericAccountsSearchFormKey.deliveryOffice },
      {
        fieldName: GenericAccountsSearchFormKey.genericAccountStatus,
      },
      {
        fieldName: GenericAccountsSearchFormKey.requestedBy,
      },
    ];

    const data: FilterProps[] = filters
      .filter(({ fieldName }) => {
        // Special case for country field
        if (fieldName === GenericAccountsSearchFormKey.deliveryOffice) {
          return (
            Array.isArray(values[fieldName]) && values[fieldName].length > 0
          );
        }
        return isNotEmpty(values[fieldName]?.toString().trim());
      })
      .map(({ fieldName, operatorIdKey }) => {
        if (fieldName === GenericAccountsSearchFormKey.deliveryOffice) {
          // Concatenate delivery office array values into a single string
          const concatenatedDeliveryOffices = values[fieldName].join("|");
          return {
            fieldName: fieldName as string,
            fieldValue: concatenatedDeliveryOffices,
            operatorId: "",
            savedSearchFilterDataType: 0,
          };
        }

        // Handle other fields
        return {
          fieldName: fieldName as string,
          fieldValue: trimValue(values[fieldName]?.toString()),
          operatorId: operatorIdKey ? values[operatorIdKey]?.toString() : "",
          savedSearchFilterDataType: 0,
        };
      });

    if (preloadFilterValuesGenericAccount?.id) {
      setPreloadFilterValuesGenericAccount({
        id: preloadFilterValuesGenericAccount.id,
        name: preloadFilterValuesGenericAccount.name,
        filters: data,
      });
    } else {
      setFiltersData(data);
    }
  };

  const onSaveAsNewFilterClickButton = () => {
    setFiltersData([...preloadFilterValuesGenericAccount.filters]);
    if (
      listOfFiltersGenericAccount &&
      listOfFiltersGenericAccount.length >= MAX_SAVE_FILTERS_LIMIT
    ) {
      setShowMultipleDeleteModal(true);
      setIsMaxFilterReached(true);
    } else {
      setPreloadFilter(false);
    }
  };

  const callbackAction = () => {
    if (isMaxFilterReached) {
      setPreloadFilter(false);
    }
    setIsOpenSaveFilterModal(true);
  };

  const handleEditAccount = (accountId: string) => {
    navigate(PAGE_URLS.EDIT_GENERIC_ACCOUNTS, {
      state: {
        genericAccountId: accountId,
      },
    });
  };

  const handleViewAccount = (accountId: string) => {
    navigate(PAGE_URLS.VIEW_GENERIC_ACCOUNT, {
      state: { genericAccountId: accountId },
    });
  };

  const handleDeleteAccountClick = (accountId: string) => {
    setAccountIdToDelete(accountId);
    setShowDeleteAccountModal(true);
  };

  const handleConfirmDeleteAccount = async () => {
    if (!accountIdToDelete) return;
    setShowDeleteAccountModal(false);
    await deleteGenericAccountMutation({
      genericAccountId: accountIdToDelete,
    });
    displayToast(
      fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.genericAccountDeleted,
      ),
      "success",
    );
    handleAccountSearchClick();
    setAccountIdToDelete(null);
  };

  const handleCancelDeleteAccount = () => {
    setShowDeleteAccountModal(false);
  };

  useEnterKeyAction({
    onEnter: handleSubmit(() => handleAccountSearchClick()),
  });

  return (
    <FormProvider {...methods}>
      <GenericAccountsSearchWrapper data-testid="client-search">
        <GenericAccountsSearchHeader />
        <GenericAccountsSearchForm
          handleSearchClick={handleAccountSearchClick}
          handleClearAllSearch={handleClearAllSearchButton}
          handleSaveFilter={handleSaveFilterModal}
          handleSavedFilter={handleSavedFilter}
          anchorRef={anchorRef}
          deliveryOfficeRef={deliveryOfficeRef}
          clientRef={clientRef}
          disableActionBtn={!listOfFiltersGenericAccount?.length}
        />
        <GenericAccountsSearchResult
          rows={clientSearchResult?.data}
          isSearch={isSearchClick}
          onEditAccountClick={handleEditAccount}
          handleViewAccountClick={handleViewAccount}
          onDeleteAccountClick={handleDeleteAccountClick}
        />
        {isOpenSaveFilterModal && !preloadFilter && (
          <SaveFilterModal
            showModal={isOpenSaveFilterModal}
            handleModalClose={closeSaveFilterModal}
            pageId={pageId}
            savedSearchPageName={4}
            filters={filtersData}
            listOfFilters={listOfFiltersGenericAccount}
            refetchListOfFilters={refetchListOfFiltersGenericAccount}
          />
        )}

        {preloadFilter && isOpenSaveFilterModal && (
          <OverwriteFilterModal
            showModal={isOpenSaveFilterModal && preloadFilter}
            handleModalClose={closeSaveFilterModal}
            handleSaveNewFilterClick={onSaveAsNewFilterClickButton}
            overrideFilterValues={preloadFilterValuesGenericAccount}
            refetchListOfFilters={refetchListOfFiltersGenericAccount}
          />
        )}

        {showSavedFilter && listOfFiltersGenericAccount && (
          <MuiMenuList
            menuArray={listOfFiltersGenericAccount}
            refValue={anchorRef.current}
            open={showSavedFilter}
            onClose={handleCloseEvent}
            onRowClick={handleAccountFilterRowClick}
            onActionBtnClick={handleDeleteFilter}
          />
        )}
        {showModal && (
          <DeleteFilterModal
            showModal={showModal}
            handleModalClose={handleFilterModalClose}
            handlePrimaryButtonClick={handlePrimaryButtonClick}
            filterToDelete={filterToDeleteGenericAccount}
            refetchListOfFilters={refetchListOfFiltersGenericAccount}
          />
        )}

        {showMultipleDeleteModal && (
          <DeleteMultipleFilterModal
            showModal={showMultipleDeleteModal}
            handleModalClose={handleMultipleFilterDeleteModalClose}
            handlePrimaryButtonClick={
              handleMultipleFilterDeletePrimaryButtonClick
            }
            listOfFilters={listOfFiltersGenericAccount}
            refetchListOfFilters={refetchListOfFiltersGenericAccount}
            callbackAction={callbackAction}
          />
        )}
        {showDeleteAccountModal && (
          <WarningConfirmationModal
            modalTitle={commonLabels.confirmation}
            show={showDeleteAccountModal}
            onClose={handleCancelDeleteAccount}
            secondaryButtonTitle={commonLabels.no}
            onPrimaryButtonClick={handleConfirmDeleteAccount}
            labelContent={fetchLabel(
              genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
              genericAccountsSearchSiteCoreKeys.areYouSureYouWantToDeleteTheGenericAccount,
            )}
          />
        )}
      </GenericAccountsSearchWrapper>
    </FormProvider>
  );
};

export default GenericAccountsSearch;
